import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Search, Calendar, Clock, ArrowRight, User, AlertCircle } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

const InsightsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { content: cmsContent, loading: cmsLoading } = useCMSContent('insights-page');

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const postsQuery = query(
        collection(db, 'cms_blog_posts'),
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc')
      );
      const snapshot = await getDocs(postsQuery);
      const firestorePosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        publishedAt: doc.data().publishedAt?.toDate?.() || new Date()
      }));
      
      // Use Firestore posts only - no fallbacks
      setPosts(firestorePosts);
    } catch (error) {
      console.error('Error loading posts:', error);
      // No fallback - show empty state
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = filteredPosts.filter(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured);

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // CMS content - no fallbacks, must be configured in CMS
  const hero = cmsContent?.hero;
  const categories = cmsContent?.categories || [];
  const newsletter = cmsContent?.newsletter;

  // Show loading state
  if (cmsLoading) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Show error if CMS content is not configured
  if (!hero) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Insights page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/insights-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero (title, subtitle), categories
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero Section - CMS controlled */}
      <section className="py-20 bg-muted/30">
        <div className="container-wide text-center">
          {hero.title && (
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {hero.title}
            </h1>
          )}
          {hero.subtitle && (
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              {hero.subtitle}
            </p>
          )}

          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder={hero.searchPlaceholder || "Search articles..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 text-lg"
            />
          </div>
        </div>
      </section>

      {/* Category Filter - CMS controlled */}
      {categories.length > 0 && (
        <section className="py-6 border-b border-border sticky top-16 bg-background/95 backdrop-blur z-10">
          <div className="container-wide">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['All', ...categories].map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="whitespace-nowrap"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {loading ? (
        <div className="py-20 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Featured Posts */}
          {featuredPosts.length > 0 && selectedCategory === 'All' && !searchTerm && (
            <section className="py-12">
              <div className="container-wide">
                <h2 className="text-2xl font-bold mb-8">Featured Articles</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {featuredPosts.slice(0, 2).map(post => (
                    <Link 
                      key={post.id} 
                      to={`/insights/${post.slug || post.id}`}
                      className="group"
                    >
                      <article className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-xl transition-all">
                        {post.featuredImage && (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={post.featuredImage}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge variant="secondary">{post.category}</Badge>
                            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">Featured</Badge>
                          </div>
                          <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-muted-foreground mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(post.publishedAt)}
                              </span>
                              {post.readTime && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {post.readTime}
                                </span>
                              )}
                            </div>
                            <span className="text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                              Read More <ArrowRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* All Posts Grid */}
          <section className="py-12">
            <div className="container-wide">
              {(featuredPosts.length > 0 && selectedCategory === 'All' && !searchTerm) && (
                <h2 className="text-2xl font-bold mb-8">Latest Articles</h2>
              )}
              
              {filteredPosts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground text-lg">No articles found matching your criteria.</p>
                  <Button 
                    variant="link" 
                    onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
                    className="mt-2"
                  >
                    Clear filters
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(selectedCategory === 'All' && !searchTerm ? regularPosts : filteredPosts).map(post => (
                    <Link 
                      key={post.id} 
                      to={`/insights/${post.slug || post.id}`}
                      className="group"
                    >
                      <article className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all h-full flex flex-col">
                        {post.featuredImage && (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={post.featuredImage}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <div className="p-6 flex-1 flex flex-col">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge variant="secondary">{post.category}</Badge>
                          </div>
                          <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-1">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(post.publishedAt)}
                            </span>
                            {post.readTime && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {post.readTime}
                              </span>
                            )}
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Newsletter CTA - CMS controlled */}
          {newsletter && (
            <section className="py-20 bg-primary text-white">
              <div className="container-wide text-center">
                {newsletter.title && (
                  <h2 className="text-3xl font-bold mb-4">{newsletter.title}</h2>
                )}
                {newsletter.subtitle && (
                  <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
                    {newsletter.subtitle}
                  </p>
                )}
                <div className="flex gap-4 justify-center max-w-md mx-auto">
                  <Input
                    type="email"
                    placeholder={newsletter.placeholder || "Enter your email"}
                    className="flex-1 bg-white text-foreground border-0"
                  />
                  <Button size="lg" className="bg-white text-primary hover:bg-white/90">
                    {newsletter.buttonText || "Subscribe"}
                  </Button>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </SiteLayout>
  );
};

export default InsightsPage;
